import { sql } from 'drizzle-orm';
import { index, integer, sqliteTable, text } from 'drizzle-orm/sqlite-core';
import { syncedColumns } from './common';

/**
 * Synced. Everything the user has to pay (spec §5 Payments): one row per bill, utility, credit
 * card or loan. Dues are projected from the schedule in core (payments/schedule.ts) and stored in
 * payment_dues only when acted on. Money is integer centavos.
 */
export const payees = sqliteTable('payees', {
  ...syncedColumns(),
  kind: text('kind', { enum: ['bill', 'utility', 'card', 'loan'] }).notNull(),
  name: text('name').notNull(),
  /** Spending category for the expense a payment writes (loans fall back to Loan payment). */
  categoryId: text('category_id'),
  /** Budget fund the payment deducts from (null = Budget's default rule). */
  fundId: text('fund_id'),
  /** v0.5: the money account payments come out of (null = Budget's "Pay from" default). */
  accountId: text('account_id'),
  /**
   * bill: the amount; utility: the starting estimate; loan: the payment per due (fixed) or the
   * planned payment (flexible). Null for cards.
   */
  amountMinor: integer('amount_minor'),
  currency: text('currency', { enum: ['PHP'] })
    .notNull()
    .default('PHP'),
  /** 'none' = no schedule (a flexible loan paid whenever, a card's dues come from statements). */
  repeat: text('repeat', { enum: ['monthly', 'weekly', 'yearly', 'none'] })
    .notNull()
    .default('monthly'),
  /** Every N months / weeks / years (custom repeats). */
  everyN: integer('every_n').notNull().default(1),
  /** Days of the month a monthly due falls on, e.g. [3, 18] (31 = last day). Cards: [payment due day]. */
  dueDays: text('due_days', { mode: 'json' })
    .$type<number[]>()
    .notNull()
    .default(sql`'[]'`),
  /** First due on or after this day; weekly repeats use its weekday, yearly its month. */
  startDate: text('start_date').notNull(),
  endDate: text('end_date'),
  /** Fixed loans and bills that end: how many payments from startDate in all. */
  paymentCount: integer('payment_count'),
  /**
   * Local day the payee was added to Tahi. Scheduled dues before it count as already paid (no
   * expense, never late); a card's first tracked statement is the first one due on or after it.
   */
  trackFrom: text('track_from').notNull(),
  /** Auto-debit: Today asks "Did <name> auto-pay ₱X?"; nothing is written until confirmed. */
  autoPay: integer('auto_pay', { mode: 'boolean' }).notNull().default(false),
  /** Days before the due date it shows as "due soon". */
  remindDays: integer('remind_days').notNull().default(3),
  // Card
  issuer: text('issuer'),
  last4: text('last4'),
  statementDay: integer('statement_day'),
  /**
   * v0.5.1: due N days after each statement closes (BDO: statement on the 13th, due 25 days
   * later). Null = the fixed due day in dueDays.
   */
  dueDaysAfterStatement: integer('due_days_after_statement'),
  creditLimitMinor: integer('credit_limit_minor'),
  /** "Current balance (optional)" when the card was added: carried into its first statement, never an expense. */
  openingBalanceMinor: integer('opening_balance_minor'),
  /**
   * A supplementary card's main cardholder (e.g. "Papa"): the statement Tahi shows is only the
   * user's own charges and installments, paid to that person ("Pay your share to Papa").
   */
  cardholder: text('cardholder'),
  // Loan
  lender: text('lender'),
  /** fixed: a set number of equal payments (e.g. an online lending app); flexible: pay any time, interest on the balance. */
  loanType: text('loan_type', { enum: ['fixed', 'flexible'] }),
  /** Borrowed amount (fixed, optional) or the balance on startDate (flexible). */
  principalMinor: integer('principal_minor'),
  /** Flexible loans: interest per month in basis points (300 = 3% a month), added at each month end. */
  interestBp: integer('interest_bp'),
  note: text('note'),
});

/**
 * Synced. A projected due the user acted on: an actual amount (utility, real statement), a real
 * card minimum, skipped, or closed ("rest won't be paid"). id = singletonId(u,
 * 'due.<payeeId>.<period>') so two devices converge. Paid / partial come from payments.
 */
export const paymentDues = sqliteTable(
  'payment_dues',
  {
    ...syncedColumns(),
    payeeId: text('payee_id').notNull(),
    /** The scheduled due date: the key that ties the row to its projected due. */
    period: text('period').notNull(),
    /** Actual amount owed (replaces the default, estimate or calculated statement). */
    amountMinor: integer('amount_minor'),
    /** Cards: the real minimum from the statement. */
    minimumMinor: integer('minimum_minor'),
    status: text('status', { enum: ['open', 'skipped', 'closed'] })
      .notNull()
      .default('open'),
  },
  (t) => [index('payment_dues_user_payee_idx').on(t.userId, t.payeeId)],
);

/**
 * Synced. One payment toward a due (a partial payment is its own row) or, for flexible loans, any
 * payment at all (dueId null). ids are singletonId(u, 'pmt.<payeeId>.<period>.<n>') for dues.
 */
export const payments = sqliteTable(
  'payments',
  {
    ...syncedColumns(),
    payeeId: text('payee_id').notNull(),
    dueId: text('due_id'),
    period: text('period'),
    /** The day the money actually left. */
    paidDate: text('paid_date').notNull(),
    amountMinor: integer('amount_minor').notNull(),
    currency: text('currency', { enum: ['PHP'] })
      .notNull()
      .default('PHP'),
    /** The ledger row it wrote (expense, or transfer for a card statement); null without Spending. */
    ledgerEntryId: text('ledger_entry_id'),
    note: text('note'),
  },
  (t) => [index('payments_user_payee_idx').on(t.userId, t.payeeId)],
);

/**
 * Synced. A card installment: one expense per month for `months` months (spec decision 06), on
 * the card's due date in each month from startMonth. Charges have deterministic ledger ids.
 */
export const installments = sqliteTable(
  'installments',
  {
    ...syncedColumns(),
    cardId: text('card_id').notNull(),
    item: text('item').notNull(),
    monthlyMinor: integer('monthly_minor').notNull(),
    months: integer('months').notNull(),
    /** 'YYYY-MM': the month of the first charge (its due date). */
    startMonth: text('start_month').notNull(),
    categoryId: text('category_id'),
    currency: text('currency', { enum: ['PHP'] })
      .notNull()
      .default('PHP'),
    note: text('note'),
  },
  (t) => [index('installments_user_card_idx').on(t.userId, t.cardId)],
);

/**
 * Synced (v0.7.4). One row per user: id = singletonId(userId, 'payments_settings'). Every column
 * has a default or is nullable, so a write may carry only the fields it sets (partial upsert).
 */
export const paymentsSettings = sqliteTable('payments_settings', {
  ...syncedColumns(),
  /** "Pay from by default": used when a payee has no account of its own (null = Budget's "Pay from"). */
  defaultAccountId: text('default_account_id'),
  /** "Remind me … days before": the starting value for new payees. */
  remindDays: integer('remind_days').notNull().default(3),
});
